// Seeds the bundled "Home - Marcus and Kim" world on first visit.
// Fetches /home-marcus-kim.json (emitted from typescript/public) and copies
// each entry into localStorage WITHOUT overwriting anything the visitor has.
// Safe to run on localhost too: keys Marcus already has are left untouched.
// Deleted library ids are tombstoned; a later visit must not restore them.
import { isHostedSite } from '../llm/hosted.js';

const BUNDLED_URL = '/home-marcus-kim.json';
const LIBRARY_STORE_KEY = 'specimen-observer:world-library-store';
// Wizard draft of the bundled world. The launched world stays. Hosted visitors
// already received this draft, so it is removed there even if storage has it.
const HOSTED_DRAFT_TO_DROP = 'draft_muc8x7bw_4a0p';

function isSecretKey(key: string): boolean {
  return /apikey|api-key/i.test(key);
}

function readKey(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

// Merge bundled drafts/worlds into an existing library store, adding only ids
// the visitor is missing. Existing entries are never replaced.
function mergeLibraryStore(bundledRaw: string): void {
  let existingRaw: string | null = null;
  try {
    existingRaw = localStorage.getItem(LIBRARY_STORE_KEY);
  } catch {
    return;
  }
  if (existingRaw === null) {
    try {
      localStorage.setItem(LIBRARY_STORE_KEY, bundledRaw);
    } catch {
      // Unwritable storage: bundled world stays unavailable this session.
    }
    return;
  }
  try {
    const existing = JSON.parse(existingRaw) as {
      drafts?: Record<string, unknown>;
      worlds?: Record<string, unknown>;
      deletedIds?: { draft?: string[]; world?: string[] };
    };
    const bundled = JSON.parse(bundledRaw) as {
      drafts?: Record<string, unknown>;
      worlds?: Record<string, unknown>;
    };
    const blocked = {
      drafts: new Set(existing.deletedIds?.draft ?? []),
      worlds: new Set(existing.deletedIds?.world ?? []),
    };
    let changed = false;
    for (const section of ['drafts', 'worlds'] as const) {
      const target = existing[section];
      const source = bundled[section];
      if (!target || typeof target !== 'object' || !source || typeof source !== 'object') continue;
      for (const [id, record] of Object.entries(source)) {
        if (id in target || blocked[section].has(id)) continue;
        target[id] = record;
        changed = true;
      }
    }
    if (changed) localStorage.setItem(LIBRARY_STORE_KEY, JSON.stringify(existing));
  } catch {
    // Visitor store unreadable: leave it untouched rather than risk damage.
  }
}

export async function ensureBundledWorld(): Promise<void> {
  try {
    if (typeof fetch !== 'function' || typeof localStorage === 'undefined') return;
    const res = await fetch(BUNDLED_URL);
    if (!res.ok) return;
    const data = (await res.json()) as { keys?: Record<string, unknown> };
    const keys = data?.keys;
    if (!keys || typeof keys !== 'object') return;
    for (const [key, value] of Object.entries(keys)) {
      if (isSecretKey(key) || typeof value !== 'string') continue;
      if (key === LIBRARY_STORE_KEY) {
        mergeLibraryStore(value);
        continue;
      }
      if (readKey(key) !== null) continue;
      try {
        localStorage.setItem(key, value);
      } catch {
        // Unwritable storage: skip the remaining keys silently.
        break;
      }
    }
    dropHostedSeedDraft();
  } catch {
    // Bundled world is optional: a missing/unreachable file is not an error.
  }
}

// The hosted site shipped with this draft already in visitor storage.
// Tombstone it so a cached bundle cannot put it back. Localhost keeps it.
function dropHostedSeedDraft(): void {
  if (!isHostedSite()) return;
  const raw = readKey(LIBRARY_STORE_KEY);
  if (!raw) return;
  try {
    const store = JSON.parse(raw) as {
      drafts?: Record<string, unknown>;
      deletedIds?: { draft?: string[]; world?: string[] };
    };
    const drafts = store.drafts ?? {};
    store.drafts = drafts;
    const hadDraft = HOSTED_DRAFT_TO_DROP in drafts;
    if (hadDraft) delete drafts[HOSTED_DRAFT_TO_DROP];
    const draftIds = Array.isArray(store.deletedIds?.draft) ? store.deletedIds.draft : [];
    const alreadyTombstoned = draftIds.includes(HOSTED_DRAFT_TO_DROP);
    if (!hadDraft && alreadyTombstoned) return;
    if (!alreadyTombstoned) draftIds.push(HOSTED_DRAFT_TO_DROP);
    store.deletedIds = {
      draft: draftIds,
      world: Array.isArray(store.deletedIds?.world) ? store.deletedIds.world : [],
    };
    localStorage.setItem(LIBRARY_STORE_KEY, JSON.stringify(store));
  } catch {
    // Unreadable store: leave the visitor's library alone.
  }
}
